import React from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import { voteHelpfulResource } from 'containers/Resources/actions';
import {
  makeSelectAllowSignIn,
  makeSelectClientDetails,
} from 'containers/Main/selectors';
import ClassNames from 'classnames';
import ShareWithDropdown from 'components/ShareWithDropdown';
import ResourcePageActions from 'components/ResourcePageActions';
import Mixpanel from 'utils/mixpanelService';
import { hidePageSidebar } from 'utils/embedded';
import LikeIcon from './images/like.svg';
import DislikeIcon from './images/dislike.svg';
import HeartEmptyIcon from './images/heart.svg';
import HeartFullIcon from './images/heart-full.svg';

const useStyles = makeStyles(() => ({
  root: {
    borderTop: '1px solid #E9ECED',
    height: 52,
    width: '100%',
    position: 'relative',
    background: 'white',
    borderBottom: '8px solid #F8F9F9',
    marginTop: 27,
    marginBottom: 14,
    '& button': {
      borderRadius: 0,
      border: 'none',
    },
    '& img': {
      height: 20,
    },
  },
  actionBox: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRight: '1px solid #E9ECED',
    position: 'relative',
    '& button, div': {
      borderRadius: 0,
      border: 'none',
    },
  },
  activeBoxLike: {
    border: '1px solid #01619B',
    color: '#01619B',
  },
  activeBoxDislike: {
    border: '1px solid #E14B4B',
  },
  shareWrapper: {
    '& div': {
      width: '100%',
    },
  },
}));

function MobileActionsPanel({ data, reviewProps }) {
  const classes = useStyles();
  const hideActionPanel = hidePageSidebar();
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const clientDetails = useSelector(makeSelectClientDetails());
  const resourceData = _get(data, 'resourceData', {});
  const actionProps = _get(data, 'actionProps', {});
  const { ratings, toggleFloatingHelpful, type } = actionProps;
  const dispatch = useDispatch();
  if (hideActionPanel) return null;
  const {
    fields: { title, slug, name, article },
    sys: { id },
  } = _get(resourceData, 'items.0');
  const { save, saved } = actionProps;
  const hasRating = !!_get(ratings, 'up') || !!_get(ratings, 'down');
  const isPositive = ratings.prevRating;
  const handleClick = value => {
    vote(value);
    toggleFloatingHelpful();
  };
  const vote = value => {
    Mixpanel.track('Resource - Was this helpful', {
      path: window.location.pathname,
      type,
      name: title || name || article,
      location: 'menu',
      answer: typeof value === 'boolean' ? value : value === 'yes',
      slug,
      entryId: id,
    });

    dispatch(
      voteHelpfulResource({
        resourceID: id,
        isThumbUp: typeof value === 'boolean' ? value : value === 'yes',
        data: {},
        name: title || name,
        slug,
        type,
      }),
    );
  };

  const shouldShowActionsButtons = !_get(
    clientDetails,
    'metadata.hideLeftPanelActions',
    false,
  );

  return (
    <Box
      component="div"
      display="flex"
      className={classes.root}
      justifyContent="space-evenly"
    >
      <Box
        component="div"
        className={ClassNames(classes.actionBox, {
          [classes.activeBoxLike]: hasRating && isPositive === true,
        })}
      >
        <ButtonBase onClick={() => handleClick(true)}>
          <img src={LikeIcon} alt="Like" className={classes.btnIcon} />
        </ButtonBase>
      </Box>
      <Box
        component="div"
        className={ClassNames(classes.actionBox, {
          [classes.activeBoxDislike]: hasRating && isPositive === false,
        })}
      >
        <ButtonBase onClick={() => handleClick(false)}>
          <img src={DislikeIcon} alt="Dislike" className={classes.btnIcon} />
        </ButtonBase>
      </Box>
      {allowSignIn && reviewProps && (
        <Box component="div" className={classes.actionBox}>
          <ResourcePageActions
            key={_get(reviewProps, 'id')}
            {...reviewProps}
            isMobile
          />
        </Box>
      )}
      {allowSignIn && (
        <Box component="div" className={classes.actionBox}>
          <ButtonBase className={ClassNames(classes.btn)} onClick={save}>
            <img
              src={saved ? HeartFullIcon : HeartEmptyIcon}
              alt="Save As Favorite"
              className={classes.btnIcon}
            />
          </ButtonBase>
        </Box>
      )}
      {shouldShowActionsButtons && (
        <Box
          component="div"
          className={ClassNames(classes.actionBox, classes.shareWrapper)}
        >
          <ShareWithDropdown
            id={id}
            name={name || title}
            type="topic"
            iconOnly
            isButton
            isBig
          />
        </Box>
      )}
    </Box>
  );
}

MobileActionsPanel.propTypes = {
  data: PropTypes.object,
  reviewProps: PropTypes.object,
};

export default MobileActionsPanel;
